import { DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

export interface KpiCard {
  label: string;
  value: number | string;
  icon: string;
  color: string; // Tailwind text color class
  bgColor: string; // Tailwind bg color class
  route?: string;
  queryParams?: any;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

@Component({
  selector: 'app-kpi-card-grid',
  standalone: true,
  imports: [DecimalPipe, NgClass, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, RouterLink],
  templateUrl: './kpi-card-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[attr.title]': 'null' },
})
export class KpiCardGridComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() widgetId!: string;
  @Input() filters: any = {};
  @Input() cards: KpiCard[] = []; // Can be provided directly OR fetched via widgetId
  @Input() title = '';
  @Input() columns = 4; // grid columns
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  loading = 0;
  error: string | null = null;

  get dataLoader(): boolean {
    return this.loading > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.widgetId && this.dashboardType) {
      this.fetchData();
    }
  }

  private fetchData(): void {
    this.loading++;
    this.error = null;
    this.engine
      .getWidgetData(this.dashboardType, this.widgetId, this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.cards = this.mapToCards(res.record?.data ?? res.record ?? res);
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.message ?? 'Failed to load data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });
  }

  private mapToCards(data: any): KpiCard[] {
    if (Array.isArray(data)) return data as KpiCard[];
    return Object.keys(data).map((key) => ({
      label: this.formatLabel(key),
      value: data[key],
      icon: 'info',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    }));
  }

  isNumber(val: number | string): boolean {
    return typeof val === 'number';
  }

  private formatLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
